import Button from "@/components/Base/Button";
import Lucide from "@/components/Base/Lucide";
import { Business } from "@/config";
import { renderTime } from "@/helper";
import { TOrder } from "@/services/chefService";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { TabButtonStatus as Status } from "@/enum/tabButtonStatus";
import { useTrackingTimer } from "@/components/hooks/useTrackingTimer";
import { translateLanguage } from "@/helper/i18n";

interface ListItemProps {
    order: TOrder;
    setSelectedOrder: (order: TOrder) => void;
    shop: Business;
    setDialogAccept: Dispatch<SetStateAction<boolean>>;
    status: Status;
}

// Helper function to safely render strings or localized object values
const renderText = (value: any): string => {
    if (!value) return "";
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (typeof value === "object") {
        // Handle multi-language object properties if applicable (e.g., value.en || value.name)
        return value.name || value.en || value.title || JSON.stringify(value);
    }
    return "";
};

export default function ListItem({ order, setSelectedOrder, shop, setDialogAccept, status }: ListItemProps) {
    const { t } = useTranslation();
    const timeText = useTrackingTimer(order?.statusTracking?.pending);

    return (
        <div className="bg-slate-100 grid grid-cols-12 p-2 my-1 rounded">
            <div className="col-span-8 flex flex-col gap-1 font-bold">

                <div className="text-[11px]">{translateLanguage(order.name, order.nameKH || order.name) || renderText(order.name)}</div>
                {order.options && order.options.length > 0 && (
                    <div className="text-[9px] font-normal text-gray-500 flex flex-col gap-0.5">
                        {order.options.map((opt, optIdx) => {
                            const optName = translateLanguage(opt.name, opt.nameKH || opt.name);
                            const valNames = (opt.values || [])
                                .map((val) => translateLanguage(val.name, val.nameKH || val.name))
                                .filter(Boolean)
                                .join(', ');
                            if (!valNames) return null;
                            return (
                                <span key={opt._id || optIdx}>
                                    {optName}: {valNames}
                                </span>
                            );
                        })}
                    </div>
                )}
                <div className="flex gap-2 items-center">
                    <div className="flex gap-1">
                        <span className="text-red-600 text-[10px]">x {order.quantity}</span>
                    </div>
                    <div className="flex gap-1 items-center justify-center text-[9px]">
                        <Lucide icon="Clock" className="w-2.5 stroke-[3]" />
                        {order?.statusTracking?.pending ? dayjs(order.statusTracking.pending).format("hh:mm A") : "--:--"}
                    </div>
                    <div className={`flex gap-1 items-center justify-center text-[9px] font-semibold ${renderTime(timeText, shop?.performanceConfig?.maxServiceTime)}`}>
                        <Lucide icon="AlarmClock" className="w-3 stroke-[2] pb-0.5" />
                        {timeText}
                    </div>
                </div>

                {order?.instruction && (
                    <div className="flex gap-1 items-center text-[9px]">
                        <Lucide icon="MessageSquareText" className="w-2 h-2" />
                        {renderText(order.instruction)}
                    </div>
                )}
            </div>
            <div className="col-span-4 flex flex-col items-end justify-between">

                <div className="text-blue-500 font-extrabold flex justify-center items-center gap-1 text-[9px]">
                    <Lucide icon="Martini" className="w-2 h-2 text-gray-900" />
                    {renderText(order.tableName)}
                </div>
                {status === 'Ready' && (
                    <div className="flex gap-1 sm:gap-2 mt-4">
                        <Button
                            type="button"
                            className="bg-green-500 text-white"
                            size="sm"
                            onClick={() => {
                                setSelectedOrder(order);
                                setDialogAccept(true);
                            }}
                        >
                            {t("done")}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
